/**
 * Diff-based selection for undo/redo: given a transition between two
 * committed rooms, select the part that changed — as it exists in the room
 * being displayed (`to`). The transition determines the object; the
 * destination merely addresses it. So undoing a delete selects the
 * resurrected segment, while redoing that delete falls back to the host edge.
 *
 * Pure edits give every committed room structural sharing with its parent, so
 * the diff is a reference walk: exactly one region differing → select it;
 * anything ambiguous (a JSON paste replaces the whole graph) → none.
 */

import { CORNER_NAMES, EDGE_NAMES, EdgeName, FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { segmentSelectionFor } from './edgeRefs';
import type { Selection } from './editorState';

type Edge = FloorPlanRoom[EdgeName];
type FreeElements = FloorPlanRoom['freeElements'];

/** The changed part of `from → to`, selected as it exists in `to`. */
export function selectionForTransition(from: FloorPlanRoom, to: FloorPlanRoom): Selection {
  if (from === to) return { kind: 'none' };
  const changes: Selection[] = [];
  if (
    from.width !== to.width || from.length !== to.length
    || from.height !== to.height || from.floorType !== to.floorType
  ) {
    changes.push({ kind: 'room' });
  }
  for (const corner of CORNER_NAMES) {
    if (from[corner] !== to[corner]) changes.push({ kind: 'corner', corner });
  }
  for (const edge of EDGE_NAMES) {
    if (from[edge] !== to[edge]) changes.push(edgeTransition(from[edge], to[edge], edge));
  }
  if (from.freeElements !== to.freeElements) {
    changes.push(freeElementsTransition(from.freeElements, to.freeElements));
  }
  return changes.length === 1 ? changes[0] : { kind: 'none' };
}

/** One edge changed: pin down which segment, or fall back to the edge itself. */
function edgeTransition(from: Edge, to: Edge, edge: EdgeName): Selection {
  const a = from?.segments ?? [], b = to?.segments ?? [];
  if (a.length === b.length) {
    const changed = a.reduce<number[]>((idxs, seg, i) => (seg === b[i] ? idxs : [...idxs, i]), []);
    if (changed.length === 1) return segmentSelectionFor(edge, changed[0], b[changed[0]]);
  } else if (b.length === a.length + 1) {
    const i = insertedIndex(a, b);
    if (i != null) return segmentSelectionFor(edge, i, b[i]);
  }
  // Removal (nothing to select in `to`) or an ambiguous multi-segment change:
  // the host edge is the changed thing.
  return { kind: 'edge', edge };
}

function freeElementsTransition(fromEls: FreeElements, toEls: FreeElements): Selection {
  const a = fromEls ?? [], b = toEls ?? [];
  if (a.length === b.length) {
    const changed = a.reduce<number[]>((idxs, el, i) => (el === b[i] ? idxs : [...idxs, i]), []);
    if (changed.length === 1) return { kind: 'inner', index: changed[0] };
  } else if (b.length === a.length + 1) {
    const i = insertedIndex(a, b);
    if (i != null) return { kind: 'inner', index: i };
  }
  // Removal or ambiguous: free elements have no host to fall back to.
  return { kind: 'none' };
}

/** Index where `longer` = `shorter` with exactly one item inserted, if so. */
function insertedIndex<T>(shorter: readonly T[], longer: readonly T[]): number | null {
  let i = 0;
  while (i < shorter.length && shorter[i] === longer[i]) i++;
  for (let j = i; j < shorter.length; j++) {
    if (shorter[j] !== longer[j + 1]) return null;
  }
  return i;
}
